import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    HeaderContainer,
    LeftBackgroundSection,
    StyledLogo,
    BreadcrumbsWrapper,
    CompanyLogoWrapper,
    ButtonGroupWrapper,
    StyledIcon,
    StyledIconButton,
    StyledMenu,
    CompanyLogoImg,
} from './TopHeader.styles';
import {
    useTheme,
    useAppSelector,
    Box,
    PargodaRoundButton,
    Badge,
    useAppDispatch,
    Grid,
    Theme,
    IconButton,
    Icon,
    MenuItem,
    Typography,
    RootState,
} from '../../../base';
import {
    jobApplicationActions,
    jobApplicationSelectors,
    jobsSelectors,
    organizationSelectors,
} from '../../../actions';
import { IJob, Directions, FeatureFlags } from '../../../common';
import Breadcrumbs from '../../EmployerPageLayout/TopHeader/Breadcrumbs';
import ApplyNowModal from '../../ApplyNowModal';
import ApplyNowModalV2 from '../../ApplyNowModalV2';
import ShareJobModal from '../../ShareJobModal';
import { useMediaQuery } from '@mui/material';
const TopHeader = () => {
    const job: IJob | null = useAppSelector(jobsSelectors.getActiveJob);
    const [applyNowModalOpen, setApplyNowModalOpen] = useState(false);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const candidateSavedJobs = useAppSelector(
        jobsSelectors.getCandidateSavedJobs
    );
    const jobIdSuccessfullyApplied = useAppSelector(
        jobApplicationSelectors.getJobIdSuccessfullyApplied
    );
    const companyInfo = useAppSelector(organizationSelectors.getCompanyInfo);
    const isCandidateQuestionsFeatureFlagOn = useAppSelector(
        (state: RootState) =>
            !!organizationSelectors.isFlagOn(
                state,
                FeatureFlags.CANDIDATE_QUESTIONS
            )
    );
    const isMobile = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('md')
    );
    const isMobileSm = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('sm')
    );
    const showSideMenu = isMobile && job?.id;
    const queryParams = new URLSearchParams(location.search);
    const applyNowIndicator = queryParams.get('applyNow') || '';

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleSideMenuClick = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleSideMenuClose = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(null);
    };

    useEffect(() => {
        if (applyNowIndicator) {
            setApplyNowModalOpen(true);
        }
        if (!job?.id && !applyNowIndicator) {
            setApplyNowModalOpen(false);
        }
    }, [job, applyNowIndicator]);

    useEffect(() => {
        if (jobIdSuccessfullyApplied) {
            setApplyNowModalOpen(false);
            dispatch(jobApplicationActions.setJobIdSuccessfullyAppliedFalse());
        }
    }, [dispatch, jobIdSuccessfullyApplied]);

    const renderSharedButton = (onClick?: () => void) => (
        <StyledIconButton
            edge="end"
            size="small"
            onClick={() => {
                onClick?.();
            }}
            borderColor={theme.palette.grey[600]}
            sx={{
                marginInlineEnd: theme.spacing(3),
            }}
        >
            <StyledIcon
                name={'ShareButtonIcon'}
                size={28}
                iconColor={theme.palette.grey[600]}
            />
        </StyledIconButton>
    );

    const renderBookmarkButton = () => (
        <StyledIconButton
            edge="end"
            size="small"
            onClick={() => {}}
            borderColor={theme.palette.secondary.main}
            sx={{
                marginInlineEnd: theme.spacing(2),
            }}
        >
            {candidateSavedJobs?.length > 0 && (
                <Badge
                    badgeContent={candidateSavedJobs?.length}
                    color="secondary"
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <StyledIcon
                        name={'BookmarkFilledIcon'}
                        size={28}
                        iconColor={theme.palette.secondary.main}
                    />
                </Badge>
            )}
            {candidateSavedJobs?.length === 0 && (
                <StyledIcon
                    name={'BookmarkButtonIcon'}
                    size={28}
                    iconColor={theme.palette.secondary.main}
                />
            )}
        </StyledIconButton>
    );

    return (
        <HeaderContainer>
            {!isMobileSm && (
                <LeftBackgroundSection>
                    <StyledLogo src="/assets/logoIcon.svg" alt="Logo" />
                </LeftBackgroundSection>
            )}
            <BreadcrumbsWrapper>
                <Breadcrumbs />
            </BreadcrumbsWrapper>
            {companyInfo?.companyLogoUrl && (
                <CompanyLogoWrapper>
                    <Grid item>
                        <CompanyLogoImg src={companyInfo.companyLogoUrl} />
                    </Grid>
                </CompanyLogoWrapper>
            )}
            <ButtonGroupWrapper>
                <Box display="flex" alignItems="center" flexDirection="row">
                    {false && !showSideMenu && renderBookmarkButton()}
                    {job?.id && (
                        <>
                            {!isMobile &&
                                renderSharedButton(() =>
                                    setShareModalOpen(true)
                                )}
                            <PargodaRoundButton
                                variant={'contained'}
                                onClick={() => {
                                    setApplyNowModalOpen(true);
                                }}
                                disabled={false}
                                buttonColor={theme.palette.common.white}
                                sx={{
                                    background: `linear-gradient(0deg,${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                    maxWidth: '28vw',
                                }}
                            >
                                <Typography variant="h6">
                                    {t('positionPage.applyToJob')}
                                </Typography>
                            </PargodaRoundButton>
                            {showSideMenu && (
                                <>
                                    <IconButton
                                        onClick={handleSideMenuClick}
                                        sx={{ height: 24, width: 24 }}
                                    >
                                        <Icon name={'MoreVertIcon'} />
                                    </IconButton>
                                    <StyledMenu
                                        anchorEl={anchorEl}
                                        open={open}
                                        onClose={handleSideMenuClose}
                                        onClick={(e) => e.stopPropagation()}
                                        anchorOrigin={{
                                            vertical: 'top',
                                            horizontal:
                                                theme.direction ==
                                                Directions.rtl
                                                    ? 'right'
                                                    : 'left',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal:
                                                theme.direction ==
                                                Directions.rtl
                                                    ? 'left'
                                                    : 'right',
                                        }}
                                    >
                                        {false && (
                                            <MenuItem onClick={() => {}}>
                                                {renderBookmarkButton()}
                                            </MenuItem>
                                        )}
                                        <MenuItem
                                            onClick={() => {
                                                setShareModalOpen(true);
                                            }}
                                        >
                                            {renderSharedButton()}
                                        </MenuItem>
                                    </StyledMenu>
                                </>
                            )}
                        </>
                    )}
                </Box>
            </ButtonGroupWrapper>
            {isCandidateQuestionsFeatureFlagOn ? (
                <ApplyNowModalV2
                    positionTitle={job?.title}
                    isModalOpen={applyNowModalOpen}
                    setIsModalOpen={setApplyNowModalOpen}
                />
            ) : (
                <ApplyNowModal
                    positionTitle={job?.title}
                    isModalOpen={applyNowModalOpen}
                    setIsModalOpen={setApplyNowModalOpen}
                />
            )}
            <ShareJobModal
                modalTitle={t('positionPage.shareJob')}
                isModalOpen={shareModalOpen}
                setIsModalOpen={setShareModalOpen}
                jobId={job?.id}
                isPublic
            />
        </HeaderContainer>
    );
};

export default TopHeader;
